import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Badge, Button, Empty, List, Spin, Typography } from "antd";
import {
  CloseOutlined,
  HistoryOutlined,
  InboxOutlined,
  LockOutlined,
  NumberOutlined,
  StarOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useChat } from "./ChatContext";
import { getMessages } from "./chatApi";
import { resolveAvatarUrl } from "./zulipClient";
import { dmConversationFromMessage, dmKeyFromUserIds } from "./useConversations";
import type { ChatViewKind, ZulipMessage, ZulipStream } from "./types";

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

const VIEW_TITLES: Record<ChatViewKind, string> = {
  inbox: "Inbox",
  recent: "Recent conversations",
  combined: "Combined feed",
  mentions: "Mentions",
  starred: "Starred messages",
};

const VIEW_NARROWS: Partial<Record<ChatViewKind, Array<{ operator: string; operand: unknown }>>> = {
  mentions: [{ operator: "is", operand: "mentioned" }],
  starred: [{ operator: "is", operand: "starred" }],
  combined: [],
  recent: [],
};

export const CHAT_VIEW_ITEMS: Array<{ key: ChatViewKind; label: string; icon: React.ReactNode }> = [
  { key: "inbox", label: "Inbox", icon: <InboxOutlined /> },
  { key: "recent", label: "Recent conversations", icon: <HistoryOutlined /> },
  { key: "combined", label: "Combined feed", icon: <UnorderedListOutlined /> },
  { key: "mentions", label: "Mentions", icon: <span aria-hidden>@</span> },
  { key: "starred", label: "Starred messages", icon: <StarOutlined /> },
];

/** Conversation key a message belongs to — for grouping "Recent conversations". */
function conversationKeyFor(msg: ZulipMessage, myUserId: number): string {
  if (msg.type === "private") {
    const convo = dmConversationFromMessage(msg, myUserId);
    return convo ? `d:${convo.key}` : `d:${msg.id}`;
  }
  return `c:${msg.stream_id}:${msg.subject ?? ""}`;
}

/**
 * Renders whichever Zulip-style "view" is active (see ChatContext.activeView).
 * "inbox" is derived entirely from state already loaded elsewhere (unread
 * counts, subscriptions, dm conversations) — no fetch needed. The other four
 * fetch a bounded window of messages via a `narrow` and render either a flat
 * feed (mentions/starred/combined) or one row per conversation (recent).
 * Clicking any row jumps straight to that channel+topic or DM thread, the
 * same way selecting it from the regular conversation list does.
 */
const ChatViewPane: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const {
    activeView,
    myUserId,
    subscriptions,
    dmConversations,
    realmUsers,
    unread,
    selectChannel,
    selectDm,
  } = useChat();

  const [feedMessages, setFeedMessages] = useState<ZulipMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeView || activeView === "inbox") return;
    const narrow = VIEW_NARROWS[activeView] ?? [];
    let cancelled = false;
    setLoading(true);
    getMessages({ narrow, anchor: "newest", numBefore: 100, numAfter: 0 })
      .then((res) => {
        if (!cancelled) setFeedMessages(res.messages);
      })
      .catch(() => {
        if (!cancelled) setFeedMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeView]);

  // Newest-first flat feed for mentions/starred/combined.
  const flatFeed = useMemo(() => [...feedMessages].sort((a, b) => b.id - a.id), [feedMessages]);

  // One row per conversation (most recent message) for "recent conversations".
  const recentConversations = useMemo(() => {
    if (myUserId === null) return [];
    const byKey = new Map<string, ZulipMessage>();
    for (const msg of feedMessages) {
      const key = conversationKeyFor(msg, myUserId);
      const existing = byKey.get(key);
      if (!existing || msg.id > existing.id) byKey.set(key, msg);
    }
    return [...byKey.values()].sort((a, b) => b.timestamp - a.timestamp);
  }, [feedMessages, myUserId]);

  const inboxChannelEntries = useMemo(() => {
    return Object.entries(unread.byChannelTopic)
      .map(([key, count]) => {
        const sep = key.indexOf(":");
        const streamId = parseInt(key.slice(0, sep), 10);
        const topic = key.slice(sep + 1);
        const stream = subscriptions.find((s) => s.stream_id === streamId);
        return { streamId, topic, count, stream };
      })
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [unread.byChannelTopic, subscriptions]);

  const inboxDmEntries = useMemo(() => {
    return Object.entries(unread.byDmKey)
      .map(([key, count]) => {
        const userIds = key.split(",").filter(Boolean).map(Number);
        const convo = dmConversations.find((c) => c.key === key);
        const label = convo?.label ?? userIds.map((id) => realmUsers.find((u) => u.user_id === id)?.full_name ?? "Unknown").join(", ");
        return { userIds, count, label };
      })
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [unread.byDmKey, dmConversations, realmUsers]);

  const jumpToMessage = (msg: ZulipMessage) => {
    if (msg.type === "private") {
      if (myUserId === null) return;
      const convo = dmConversationFromMessage(msg, myUserId);
      if (convo) selectDm(convo.userIds, convo.label);
      return;
    }
    if (msg.stream_id === undefined) return;
    // Stream messages' display_recipient is the stream name itself — used
    // even when the stream isn't in `subscriptions` (e.g. a mention in a
    // channel joined after unsubscribing, or one only browsed, not joined).
    const streamName = typeof msg.display_recipient === "string" ? msg.display_recipient : `stream-${msg.stream_id}`;
    const stream: ZulipStream = subscriptions.find((s) => s.stream_id === msg.stream_id) ?? { stream_id: msg.stream_id, name: streamName };
    selectChannel(stream, msg.subject ?? "");
  };

  const jumpToChannelEntry = (streamId: number, topic: string, stream?: ZulipStream) => {
    selectChannel(stream ?? { stream_id: streamId, name: `stream-${streamId}` }, topic);
  };

  if (!activeView) return null;

  // "Recent conversations" shows one row per *conversation* (its most recent
  // message), so whether that row is unread has to come from the
  // conversation-level counts — the same ones the sidebar's own dot uses.
  const unreadCountForMessage = (msg: ZulipMessage): number => {
    if (msg.type === "private") {
      if (myUserId === null) return 0;
      const convo = dmConversationFromMessage(msg, myUserId);
      return convo ? unread.byDmKey[convo.key] ?? 0 : 0;
    }
    if (msg.stream_id === undefined) return 0;
    return unread.byChannelTopic[`${msg.stream_id}:${msg.subject ?? ""}`] ?? 0;
  };

  const renderMessageRow = (msg: ZulipMessage, opts?: { unread?: boolean }) => {
    const isDm = msg.type === "private";
    const streamName = typeof msg.display_recipient === "string" ? msg.display_recipient : undefined;
    const contextLabel = isDm ? undefined : `#${streamName ?? msg.stream_id} › ${msg.subject ?? ""}`;
    return (
      <List.Item key={msg.id} className="chat-row" onClick={() => jumpToMessage(msg)}>
        <Avatar size={36} src={resolveAvatarUrl(msg.avatar_url) || undefined} style={{ flexShrink: 0 }}>
          {!msg.avatar_url && msg.sender_full_name.charAt(0).toUpperCase()}
        </Avatar>
        <div className="chat-row__main">
          <div className="chat-row__title-line">
            <Typography.Text strong ellipsis className="chat-row__title">
              {msg.sender_full_name}
            </Typography.Text>
            <span className="chat-row__time">{dayjs.unix(msg.timestamp).format("M/D h:mm A")}</span>
          </div>
          {contextLabel && (
            <Typography.Text type="secondary" ellipsis className="chat-row__preview">
              {contextLabel}
            </Typography.Text>
          )}
          <Typography.Text type="secondary" ellipsis className="chat-row__preview">
            {stripHtml(msg.content)}
          </Typography.Text>
        </div>
        {opts?.unread && <span className="chat-unread-dot" />}
      </List.Item>
    );
  };

  return (
    <div className="chat-view-pane">
      <div className="chat-view-pane__header">
        <Typography.Text strong>{VIEW_TITLES[activeView]}</Typography.Text>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
      </div>
      <div className="chat-view-pane__scroll">
        {activeView === "inbox" ? (
          inboxChannelEntries.length === 0 && inboxDmEntries.length === 0 ? (
            <Empty description="You're all caught up" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              {inboxDmEntries.length > 0 && (
                <>
                  <div className="chat-conversation-list__section-title">Direct Messages</div>
                  <List
                    size="small"
                    dataSource={inboxDmEntries}
                    renderItem={(e) => (
                      <List.Item className="chat-row" onClick={() => selectDm(e.userIds, e.label)}>
                        <Avatar size={36} icon={<TeamOutlined />} style={{ flexShrink: 0 }} />
                        <div className="chat-row__main">
                          <Typography.Text strong ellipsis className="chat-row__title">
                            {e.label}
                          </Typography.Text>
                        </div>
                        <Badge count={e.count} size="small" />
                      </List.Item>
                    )}
                  />
                </>
              )}
              {inboxChannelEntries.length > 0 && (
                <>
                  <div className="chat-conversation-list__section-title">Channels</div>
                  <List
                    size="small"
                    dataSource={inboxChannelEntries}
                    renderItem={(e) => (
                      <List.Item className="chat-row" onClick={() => jumpToChannelEntry(e.streamId, e.topic, e.stream)}>
                        <Avatar size={36} shape="square" icon={e.stream?.invite_only ? <LockOutlined /> : <NumberOutlined />} style={{ flexShrink: 0 }} />
                        <div className="chat-row__main">
                          <Typography.Text strong ellipsis className="chat-row__title">
                            {e.stream?.name ?? `stream-${e.streamId}`}
                          </Typography.Text>
                          <Typography.Text type="secondary" ellipsis className="chat-row__preview">
                            {e.topic}
                          </Typography.Text>
                        </div>
                        <Badge count={e.count} size="small" />
                      </List.Item>
                    )}
                  />
                </>
              )}
            </>
          )
        ) : loading ? (
          <div className="chat-thread__loading">
            <Spin size="small" />
          </div>
        ) : activeView === "recent" ? (
          recentConversations.length === 0 ? (
            <Empty description="No recent conversations" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List size="small" dataSource={recentConversations} renderItem={(msg) => renderMessageRow(msg, { unread: unreadCountForMessage(msg) > 0 })} />
          )
        ) : flatFeed.length === 0 ? (
          <Empty description={`No ${VIEW_TITLES[activeView].toLowerCase()}`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          // Each row here is an individual message (not a conversation), so
          // its own "read" flag — not the conversation-level unread count —
          // says whether it should still show a dot.
          <List size="small" dataSource={flatFeed} renderItem={(msg) => renderMessageRow(msg, { unread: !msg.flags?.includes("read") })} />
        )}
      </div>
    </div>
  );
};

export default ChatViewPane;
